import React, { useEffect, useRef, useState } from 'react';
import useIsMobile from '../../hooks/useIsMobile';
import { useSelector } from 'react-redux';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import { Button, Popover, message } from 'antd';
import { CheckOutlined, StarOutlined, EllipsisOutlined } from '@ant-design/icons';
import EllipsisMenu from '../EllipsisMenu/EllipsisMenu';
import quizClient from '../../client/QuizClient';
import useTranslation from '../../hooks/useTranslation';
import './QuizCard.css';

function QuizCard({
                      quiz,
                      isMultipleCorrect,
                      selectedOptions,
                      handleOptionChange,
                      checked,
                      handleCheckClick,
                      handleNextClick,
                      isAnswerCorrect,
                  }) {
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    const selectedDeck = useSelector((state) => state.deck.selectedDeck);
    const mainLanguage = selectedDeck?.locale || { name: 'Original', flag: null };
    const translationLanguage = selectedDeck?.translateLocale || { name: 'Translate', flag: null };

    const [buttonMode, setButtonMode] = useState('check');
    const [showTranslated, setShowTranslated] = useState(false);
    const [lastClickTime, setLastClickTime] = useState(0);
    const [selectedLabel, setSelectedLabel] = useState(quiz.label || null);
    const [isLabelUpdating, setIsLabelUpdating] = useState(false);

    // На прогоне практики квиз приходит из /api/practice, где его id лежит в itemId.
    const quizId = quiz.itemId || quiz.id;

    // Компонент переживает переход к следующему вопросу — меняется только quiz. Ответ на
    // запрос метки может прийти уже после этого, и без сверки он покрасил бы новый вопрос
    // цветом предыдущего.
    const shownQuizIdRef = useRef(quizId);

    useEffect(() => {
        shownQuizIdRef.current = quizId;
        setSelectedLabel(quiz.label || null);
    }, [quiz.label, quizId]);

    // Метка обязана пережить переход к следующему вопросу, поэтому её сразу пишет сервер.
    // Показываем выбор до ответа, а на ошибке возвращаем прежний цвет: иначе карточка
    // утверждала бы, что метка сохранена, когда её нет.
    const handleLabelChange = async (label) => {
        if (isLabelUpdating || !quizId) {
            return;
        }

        const labelledQuizId = quizId;
        const previousLabel = selectedLabel;
        setSelectedLabel(label);
        setIsLabelUpdating(true);

        try {
            const updatedQuiz = await quizClient.updateQuizLabel(labelledQuizId, label);
            if (shownQuizIdRef.current === labelledQuizId) {
                setSelectedLabel(updatedQuiz?.label || null);
            }
        } catch (error) {
            if (shownQuizIdRef.current === labelledQuizId) {
                setSelectedLabel(previousLabel);
                message.error('Failed to update label.');
            }
            console.error('Error updating quiz label:', error);
        } finally {
            setIsLabelUpdating(false);
        }
    };

    const handleButtonClick = () => {
        if (buttonMode === 'check') {
            handleCheckClick();
            setButtonMode('next');
        } else {
            handleNextClick();
            setButtonMode('check');
        }
    };

    const handleQuestionClick = () => {
        const now = Date.now();
        if (now - lastClickTime < 300) {
            if (mainLanguage.name !== translationLanguage.name) {
                setShowTranslated((prev) => !prev);
            }
        }
        setLastClickTime(now);
    };

    const getDisplayedQuestion = () =>
        showTranslated
            ? quiz.translateQuestion || '[Translation not available.]'
            : quiz.question;

    const getDisplayedAnswer = (answer) =>
        showTranslated
            ? answer.translateText || '[Translation not available.]'
            : answer.text;

    /**
     * Рамка варианта: до проверки выбранный обводится серым, после проверки правильный
     * зелёным, а ошибочно выбранный красным. Невыбранное остаётся без рамки.
     */
    const answerBorder = (answer) => {
        if (!checked) {
            return selectedOptions.has(answer.text) ? '2px solid grey' : '2px solid transparent';
        }
        if (isAnswerCorrect(answer.text)) {
            return '2px solid green';
        }
        return selectedOptions.has(answer.text) ? '2px solid #b22222' : '2px solid transparent';
    };

    const getFormControlLabelStyle = (answer) => ({
        margin: isMobile ? '2px 0' : '10px 0',
        border: answerBorder(answer),
        borderRadius: '4px',
        padding: '5px',
        maxWidth: '100%',
    });

    return (
        <div style={{ position: 'relative', padding: isMobile ? '0' : '10px' }}>
            <Card>
                <CardContent>
                    <Typography
                        variant={isMobile ? 'body1' : 'h5'}
                        style={{
                            fontSize: isMobile ? '110%' : undefined,
                            marginBottom: '20px',
                            cursor:
                                quiz.translateQuestion && quiz.translateQuestion.trim() !== ''
                                    ? 'pointer'
                                    : 'default',
                        }}
                        onClick={handleQuestionClick}
                    >
                        {getDisplayedQuestion()}
                    </Typography>
                    {/* Ниже 768px радиокнопки и чекбоксы скрыты — ответ выбирается нажатием
                        на саму строку, а выбранный отмечен рамкой (getFormControlLabelStyle).
                        На планшетной ширине рамка читается слабее точки радиокнопки, но
                        механика цела, поэтому отдельного порога под 600-768px нет. */}
                    <FormControl component="fieldset" fullWidth margin="normal">
                        {isMultipleCorrect ? (
                            quiz.answers.map((answer, index) => (
                                <FormControlLabel
                                    key={answer.text}
                                    control={
                                        <Checkbox
                                            checked={selectedOptions.has(answer.text)}
                                            onChange={handleOptionChange}
                                            style={{ display: isMobile ? 'none' : 'inline-flex' }}
                                            value={answer.text}
                                        />
                                    }
                                    label={
                                        <Typography
                                            style={{
                                                wordWrap: 'break-word',
                                                maxWidth: '100%',
                                                hyphens: 'auto',
                                            }}
                                        >
                                            {getDisplayedAnswer(answer)}
                                        </Typography>
                                    }
                                    style={getFormControlLabelStyle(answer)}
                                />
                            ))
                        ) : (
                            <RadioGroup
                                aria-label="quiz-options"
                                name="quiz-options"
                                value={[...selectedOptions][0] || ''}
                                onChange={handleOptionChange}
                            >
                                {quiz.answers.map((answer, index) => (
                                    <FormControlLabel
                                        key={answer.text}
                                        value={answer.text}
                                        control={
                                            <Radio
                                                style={{ display: isMobile ? 'none' : 'inline-flex' }}
                                            />
                                        }
                                        label={
                                            <Typography
                                                style={{
                                                    wordWrap: 'break-word',
                                                    maxWidth: '100%',
                                                    hyphens: 'auto',
                                                }}
                                            >
                                                {getDisplayedAnswer(answer)}
                                            </Typography>
                                        }
                                        style={getFormControlLabelStyle(answer)}
                                    />
                                ))}
                            </RadioGroup>
                        )}
                    </FormControl>

                    <div
                        style={{
                            display: 'flex',
                            justifyContent: isMobile ? 'center' : 'flex-end',
                            marginTop: '20px',
                        }}
                    >
                        <Button
                            startIcon={buttonMode === 'check' ? <CheckOutlined /> : null}
                            endIcon={buttonMode === 'next' ? <StarOutlined /> : null}
                            onClick={handleButtonClick}
                            style={{
                                width: isMobile ? '100%' : '200px',
                            }}
                        >
                            {buttonMode === 'check' ? 'Check' : 'Next'}
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <div
                style={{
                    position: 'absolute',
                    top: isMobile ? 10 : 25,
                    right: isMobile ? 5 : 20,
                }}
            >
                {/* Popover, а не Dropdown: внутри не пункты меню, а свой компонент, и
                    Popover принимает его готовым узлом — без функции, которую линтер
                    читает как компонент, объявленный внутри компонента. */}
                <Popover
                    content={(
                        <EllipsisMenu
                            quiz={quiz}
                            mainLanguage={mainLanguage}
                            translationLanguage={translationLanguage}
                            showTranslated={showTranslated}
                            selectedLabel={selectedLabel}
                            onLabelChange={handleLabelChange}
                        />
                    )}
                    trigger="click"
                    placement="bottomRight"
                    arrow={false}
                    rootClassName="quizCard__menu"
                >
                    <button
                        type="button"
                        aria-label={t('common.moreActions')}
                        style={{
                            cursor: 'pointer',
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            lineHeight: 0,
                        }}
                    >
                        <EllipsisOutlined
                            style={{
                                fontSize: 18,
                                opacity: 0.2,
                                transform: 'rotate(90deg)',
                            }}
                        />
                    </button>
                </Popover>
            </div>
        </div>
    );
}

export default QuizCard;
